import { useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-hot-toast';
import ProductSelectModal from './ProductSelectModal';
import useOperationStore from '../../store/useOperationStore';
import { formatCurrency } from '../../utils/formatters';

const inputClass = 'mt-1 w-full rounded-xl border border-slate-200 p-3';
const currencyOptions = ['TRY', 'USD', 'EUR', 'GBP'];
const labels = { origin: 'Origin', freight: 'Navlun', delivery: 'Teslimat' };

const QuickMaterialConsumptionModal = ({ operation, stage, onClose, onSaved }) => {
  const [productOpen, setProductOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    inventoryItemId: '',
    name: '',
    unit: 'Adet',
    quantity: 1,
    unitPrice: 0,
    currency: 'TRY',
    stockQuantity: 0
  });
  const submitting = useRef(false);
  const createStageConsumption = useOperationStore((state) => state.createStageConsumption);
  const totalAmount = Math.round((Number(draft.unitPrice || 0) * Number(draft.quantity || 0) + Number.EPSILON) * 100) / 100;

  const selectProduct = (product) => {
    setDraft({
      inventoryItemId: product._id,
      name: product.name,
      unit: product.unit || 'Adet',
      quantity: 1,
      unitPrice: Number(product.purchasePrice || 0),
      currency: product.purchaseCurrency || 'TRY',
      stockQuantity: Number(product.stockQuantity || 0)
    });
    setProductOpen(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!draft.inventoryItemId) {
      toast.error('Stoktan malzeme seçin.');
      return;
    }
    if (!Number.isFinite(Number(draft.quantity)) || Number(draft.quantity) <= 0) {
      toast.error('Geçerli bir miktar girin.');
      return;
    }
    if (Number(draft.quantity) > Number(draft.stockQuantity || 0)) {
      toast.error(`Yetersiz stok. Mevcut: ${draft.stockQuantity} ${draft.unit}`);
      return;
    }
    if (!Number.isFinite(Number(draft.unitPrice)) || Number(draft.unitPrice) < 0) {
      toast.error('Geçerli bir birim fiyat girin.');
      return;
    }

    submitting.current = true;
    setSaving(true);
    const result = await createStageConsumption(operation._id, stage, {
      inventoryItemId: draft.inventoryItemId,
      quantity: Number(draft.quantity),
      unit: draft.unit,
      unitPrice: Number(draft.unitPrice),
      totalAmount,
      currency: draft.currency,
      exchangeRate: draft.currency === 'TRY' ? 1 : Number(draft.exchangeRate || 1)
    });
    if (result.success) {
      toast.success('Sarf kaydı oluşturuldu.');
      onClose();
      await onSaved();
    } else {
      toast.error(result.message);
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
      <form onSubmit={submit} className="w-full max-w-xl space-y-5 rounded-3xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-black">Malzeme Ekle</h2>
            <p className="text-xs font-bold text-slate-400">{labels[stage] || stage} aşamasına stok çıkışı</p>
          </div>
          <button type="button" onClick={onClose} className="font-bold text-slate-400">Kapat</button>
        </div>

        <button type="button" onClick={() => setProductOpen(true)} className="w-full rounded-2xl border border-dashed border-slate-300 px-4 py-4 text-left">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Stoktan Malzeme Seçimi</p>
          <p className="mt-1 font-black text-slate-800">{draft.name || 'Malzeme seçin'}</p>
          {draft.inventoryItemId ? (
            <p className="text-xs font-bold text-slate-500">Mevcut stok: {draft.stockQuantity} {draft.unit}</p>
          ) : null}
        </button>

        <fieldset disabled={saving} className="grid gap-4 md:grid-cols-2">
          <label>
            Miktar
            <input
              className={inputClass}
              type="number"
              min="0"
              step="any"
              value={draft.quantity}
              onChange={(event) => setDraft((prev) => ({ ...prev, quantity: event.target.value }))}
            />
          </label>
          <label>
            Para Birimi
            <select
              className={inputClass}
              value={draft.currency}
              onChange={(event) => setDraft((prev) => ({ ...prev, currency: event.target.value }))}
            >
              {currencyOptions.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
            </select>
          </label>
          <label className="md:col-span-2">
            Birim Fiyat
            <NumericFormat
              className={inputClass}
              value={draft.unitPrice}
              thousandSeparator="."
              decimalSeparator=","
              allowedDecimalSeparators={[',', '.']}
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              inputMode="decimal"
              suffix={` ${draft.currency}`}
              onValueChange={({ floatValue }, source) => {
                if (source.source === 'event') {
                  setDraft((prev) => ({ ...prev, unitPrice: floatValue ?? 0 }));
                }
              }}
            />
          </label>
        </fieldset>

        <p className="text-lg font-black">Toplam: {formatCurrency(totalAmount, draft.currency)}</p>
        <div className="flex gap-3">
          <button disabled={saving} className="rounded-xl bg-blue-600 px-5 py-3 font-bold text-white">
            {saving ? 'Kaydediliyor...' : 'Stoktan Çık'}
          </button>
          <button disabled={saving} type="button" onClick={onClose}>Vazgeç</button>
        </div>
      </form>
      <ProductSelectModal isOpen={productOpen} onClose={() => setProductOpen(false)} onSelect={selectProduct} forceType="Material" />
    </div>
  );
};

export default QuickMaterialConsumptionModal;
